import { useCallback, useEffect, useState } from 'react';
import parsePhoneNumberFromString, { E164Number, PhoneNumber } from 'libphonenumber-js';
import FormInputPhoneNumber from '@prezzee/ribbon-ui/lib/components/FormInputPhoneNumber';
import { CountryCode } from '@prezzee/ribbon-ui/lib/components/FormInputPhoneNumber/countries';

import { COUNTRY, envUtil } from 'common/util';
import { validateMobileNumber } from 'bindings/phoneNumberUtils';

interface PhoneValueFormat {
  countryCode?: string;
  number: E164Number;
}

interface PhoneFieldProps {
  defaultCountry?: CountryCode;
  defaultNumber?: string;
  disabled?: boolean;
  error?: string;
  fieldOnBlur?: (value?: PhoneValueFormat) => void;
  fieldOnChange: (value?: PhoneValueFormat) => void;
  mobileOnly?: boolean;
}

const PhoneField = ({
  defaultCountry,
  defaultNumber,
  disabled,
  error,
  fieldOnBlur,
  fieldOnChange,
  mobileOnly = true,
}: PhoneFieldProps): JSX.Element => {
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | null>(() => {
    if (defaultNumber) return parsePhoneNumberFromString(defaultNumber);
    return null;
  });
  const [hasUserInput, setHasUserInput] = useState<boolean>(false);
  const [placeholderNumber, setPlaceholderNumber] = useState<string>('');

  const inputCountry = defaultCountry?.toUpperCase() || envUtil.getCountry();
  let countryCode: CountryCode = 'AU';
  switch (inputCountry) {
    case COUNTRY.UnitedStates:
      countryCode = 'US';
      break;
    case COUNTRY.GreatBritain:
      countryCode = 'GB';
      break;
    case COUNTRY.NewZealand:
      countryCode = 'NZ';
      break;
  }

  const reset = () => {
    setHasUserInput(false);
    setPlaceholderNumber('');
    setPhoneNumber(null);
  };

  const parsePhoneNumber = useCallback(() => {
    if (!phoneNumber) {
      if (placeholderNumber) {
        return { number: placeholderNumber, countryCode: '' };
      }
      return null;
    }

    const { country, number } = phoneNumber;
    if (country && number) {
      const isValidNumber = mobileOnly ? validateMobileNumber(country, number) : true;
      if (isValidNumber) {
        return {
          countryCode: country,
          number,
        };
      }
    } else if (hasUserInput) {
      return { number: number ?? placeholderNumber, countryCode: country ?? '' };
    } else {
      return null;
    }
  }, [hasUserInput, phoneNumber, placeholderNumber]);

  const blurHandler = () => {
    fieldOnBlur?.(parsePhoneNumber());
  };

  const changeHandler = (value: string = '') => {
    const codeAndNumber = value.split(' ');
    if (codeAndNumber.length < 2) {
      // only has country code from dropdown
      reset();
    } else {
      setHasUserInput(true);
      setPlaceholderNumber(codeAndNumber[1].replace(/\s/g, ''));
      setPhoneNumber(parsePhoneNumberFromString(value) ?? null);
    }
  };

  useEffect(() => {
    fieldOnChange(parsePhoneNumber());
  }, [hasUserInput, phoneNumber, placeholderNumber]);

  return (
    <FormInputPhoneNumber
      defaultCountry={countryCode}
      defaultNumber={phoneNumber?.number?.toString()}
      disabled={disabled}
      countryCodes="all"
      errorMessage={error}
      onBlur={blurHandler}
      onChange={changeHandler}
    />
  );
};

export default PhoneField;
